import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';

import { Breadcrumb } from './breadcrumb.interface';
import { BreadcrumbOverrideService } from './breadcrumb-override.service';

/**
 * The signposted trail above a page.
 *
 * Ported from evo-accounts, where the trail and the page header were one
 * component. They are split here because this shell already owns its own
 * chrome: the rail says where you can go and the top bar says who you are, so
 * a header component would have had one job and a wrapper's worth of markup
 * around it.
 *
 * The trail comes from `data.breadcrumbs` on the deepest matched route, read
 * fresh on every NavigationEnd. Route data is static, which is right for
 * everything whose name is known before it loads; a page whose leaf is a
 * record -- an airport, a handler, an account -- names itself through
 * `BreadcrumbOverrideService` once it has the record.
 */
@Component({
  selector: 'app-v2-breadcrumbs',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './breadcrumbs.component.html',
  styleUrl: './breadcrumbs.component.scss',
})
export class V2BreadcrumbsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly override = inject(BreadcrumbOverrideService);

  private readonly routeBreadcrumbs = signal<Breadcrumb[]>(this.collectBreadcrumbs());

  readonly breadcrumbs = computed<Breadcrumb[]>(() => {
    // A page that supplies its whole trail has already said everything the
    // positional patches below could; applying them on top would only
    // overwrite crumbs it deliberately chose.
    const trail = this.override.trail();
    if (trail?.length) {
      return trail;
    }

    const crumbs = this.routeBreadcrumbs();
    const lastLabel = this.override.lastLabel();
    const lastRoute = this.override.lastRoute();

    if (crumbs.length > 0 && (lastLabel || lastRoute)) {
      const idx = crumbs.length - 1;
      return [
        ...crumbs.slice(0, idx),
        {
          ...crumbs[idx],
          ...(lastLabel ? { label: lastLabel } : {}),
          ...(lastRoute ? { route: lastRoute } : {}),
        },
      ];
    }

    return crumbs;
  });

  constructor() {
    // Clear a stale override the moment navigation starts. The next page sets
    // its own once its record arrives; until then the route's own declaration
    // is the honest answer, and the previous page's record name is not.
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.override.reset());

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.routeBreadcrumbs.set(this.collectBreadcrumbs()));
  }

  private collectBreadcrumbs(): Breadcrumb[] {
    let node: ActivatedRouteSnapshot | null = this.route.snapshot;
    while (node?.firstChild) {
      node = node.firstChild;
    }
    return (node?.data?.['breadcrumbs'] as Breadcrumb[] | undefined) ?? [];
  }
}
